import { useEffect, useRef, useState } from 'react'
import { Camera, Upload } from 'lucide-react'
import { apiClient } from '@/lib/api'
import { resolveApiUrl } from '@/lib/apiUrl'
import type { ApiJobPhoto } from '@/lib/apiTypes'
import { usePermissions } from '@/hooks/usePermission'
import { useOnline } from '@/hooks/useOnline'
import { Modal } from '@/components/ui/Modal'

export interface JobPhotoSource {
  list(jobId: string): Promise<ApiJobPhoto[]>
  upload(jobId: string, file: File, type: ApiJobPhoto['type']): Promise<ApiJobPhoto>
}
const liveSource: JobPhotoSource = {
  async list(id) { return (await apiClient.get(`/api/jobs/${id}/photos`)).data.data },
  async upload(id, file, type) {
    const body = new FormData()
    body.append('type', type)
    body.append('photo', file)
    return (await apiClient.post(`/api/jobs/${id}/photos`, body, { headers: { 'Content-Type': 'multipart/form-data' } })).data.data
  },
}
const ACCEPT = 'image/jpeg,image/png,image/webp'
const LIMIT = 10 * 1024 * 1024

export function JobPhotoImage({ photo }: { photo: ApiJobPhoto }) {
  const [failed, setFailed] = useState(false)
  return failed
    ? <span role="img" aria-label="Photo unavailable" className="block p-4 text-sm">Photo unavailable</span>
    : <img src={resolveApiUrl(photo.photoUrl)} crossOrigin={photo.photoUrl.startsWith('/api/') ? 'use-credentials' : undefined} alt={`${photo.type === 'AFTER' ? 'After' : 'Before'} work`} onError={() => setFailed(true)} className="w-full rounded-xl object-cover" />
}

/** Same UI in live job details and the isolated, in-memory review fixture. */
export function JobPhotos({ jobId, source = liveSource, canUpload, onPhotoUploaded }: {
  jobId: string
  source?: JobPhotoSource
  canUpload?: boolean
  onPhotoUploaded?: () => void | Promise<void>
}) {
  const permissions = usePermissions()
  const allowed = canUpload ?? (permissions.can('jobs.edit') || permissions.can('jobs.complete'))
  const online = useOnline()
  const [photos, setPhotos] = useState<ApiJobPhoto[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [uploadError, setUploadError] = useState('')
  const [busy, setBusy] = useState(false)
  const [phase, setPhase] = useState<ApiJobPhoto['type']>('BEFORE')
  const [retry, setRetry] = useState(0)
  const [selected, setSelected] = useState<ApiJobPhoto | null>(null)
  const [notice, setNotice] = useState('')
  const camera = useRef<HTMLInputElement>(null)
  const library = useRef<HTMLInputElement>(null)
  const uploading = useRef(false)
  const generation = useRef(0)

  useEffect(() => {
    const version = ++generation.current
    setPhotos([]); setLoading(true); setError(''); setSelected(null); setNotice(''); setUploadError('')
    source.list(jobId).then((items) => {
      if (version === generation.current) setPhotos(items)
    }).catch(() => {
      if (version === generation.current) setError('Could not load job photos. Try again.')
    }).finally(() => { if (version === generation.current) setLoading(false) })
    return () => { generation.current++ }
  }, [jobId, source, retry])

  async function upload(files: FileList | null) {
    if (!files?.length || uploading.current || !online || !allowed) return
    const version = generation.current
    uploading.current = true; setBusy(true); setUploadError(''); setNotice('')
    let count = 0
    const errors: string[] = []
    try {
      for (const file of Array.from(files)) {
        if (!ACCEPT.split(',').includes(file.type) || file.size > LIMIT || file.size === 0) {
          errors.push(`${file.name}: choose a JPEG, PNG or WebP up to 10 MB.`)
          continue
        }
        try {
          const photo = await source.upload(jobId, file, phase)
          if (version !== generation.current) break
          setPhotos((previous) => [photo, ...previous.filter((p) => p.id !== photo.id)])
          count++
        } catch { errors.push(`${file.name}: not saved. Select it again to retry.`) }
      }
      if (version === generation.current) {
        setUploadError(errors.join(' '))
        if (count) {
          setNotice(`${count} photo${count === 1 ? '' : 's'} attached to job.`)
          // Refresh failure must not turn a saved photo into an upload failure.
          void Promise.resolve(onPhotoUploaded?.()).catch(() => {})
        }
      }
    } finally { uploading.current = false; setBusy(false) }
  }

  return <section className="ds-tile-inset rounded-2xl p-4 space-y-4" aria-label="Job photos">
    <h3 className="font-semibold">Job photos{photos.length ? ` · ${photos.length}` : ''}</h3>
    {loading && <p role="status">Loading job photos…</p>}
    {error && <div role="alert"><p>{error}</p><button type="button" className="btn-secondary mt-2 min-h-12 px-4" disabled={busy} onClick={() => setRetry((n) => n + 1)}>Retry photos</button></div>}
    {!loading && !error && photos.length === 0 && <p>No job photos yet. Add a before or after photo.</p>}
    {photos.length > 0 && <div className="grid grid-cols-2 gap-3">{photos.map((photo) => <button key={photo.id} type="button" className="text-left min-w-0" aria-label={`View ${photo.type.toLowerCase()} job photo`} onClick={() => setSelected(photo)}>
      <div className="aspect-square overflow-hidden rounded-xl"><JobPhotoImage photo={photo} /></div>
      <span className="text-sm">{photo.type === 'AFTER' ? 'After' : 'Before'}</span>
    </button>)}</div>}
    {allowed ? <>
      <label className="block text-sm">Photo phase<select aria-label="Photo phase" className="input-field mt-1 w-full" value={phase} disabled={busy} onChange={(e) => setPhase(e.target.value as ApiJobPhoto['type'])}><option value="BEFORE">Before</option><option value="AFTER">After</option></select></label>
      <input ref={camera} aria-label="Camera photo" type="file" accept={ACCEPT} capture="environment" className="sr-only" disabled={busy || !online} onChange={(e) => { void upload(e.target.files); e.target.value = '' }} />
      <input ref={library} aria-label="Upload job photos" type="file" accept={ACCEPT} multiple className="sr-only" disabled={busy || !online} onChange={(e) => { void upload(e.target.files); e.target.value = '' }} />
      <div className="flex flex-wrap gap-2">
        <button type="button" className="btn-primary flex-1 min-h-12" disabled={busy || !online || loading} onClick={() => camera.current?.click()}><Camera size={18} aria-hidden />Camera</button>
        <button type="button" className="btn-secondary flex-1 min-h-12 px-4" disabled={busy || !online || loading} onClick={() => library.current?.click()}><Upload size={18} aria-hidden />Upload</button>
      </div>
      <p className="text-sm">JPEG, PNG or WebP · Up to 10 MB each</p>
      {!online && <p role="status">You're offline. Photos are not queued; reconnect to upload.</p>}
    </> : <p className="text-sm">You can view photos. Upload access is required to add photos.</p>}
    {busy && <p role="status">Uploading photos… Keep this job open.</p>}
    {uploadError && <p role="alert" className="break-words">{uploadError}</p>}
    {notice && <p role="status">{notice}</p>}
    <Modal isOpen={selected !== null} onClose={() => setSelected(null)} title="Job photo">{selected && <JobPhotoImage key={selected.id} photo={selected} />}</Modal>
  </section>
}
